import { useCallback, useEffect } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

type Params = {
	videoRef: React.RefObject<HTMLVideoElement | null>;
	// True while autoplay fell back to muted (see useVideoScrollTriggeredAutoplay). Can go stale —
	// the user may unmute/re-mute via the native controls, which don't touch this flag directly;
	// the volumechange listener below and the videoRef.current?.muted checks guard against that.
	startedMuted: boolean;
	// called once startedMuted should be cleared — either because a click promoted it to sound, or
	// because we noticed (via 'volumechange') that the user already changed the volume some other way
	onPromoted: () => void;
	startPlayback: (onStarted?: () => void) => void;
};

type Result = {
	handlePlayClick: () => void;
	// click on the <video> element itself — only acts while playback is muted-autoplaying, to avoid
	// fighting the native controls' own click-to-toggle-play/pause once truly unmuted
	handleVideoClick: () => void;
};

/**
 * A click is a real user gesture, so it can unlock audio that autoplay itself couldn't — while
 * muted-autoplaying, clicking the video (or its overlay) promotes it to sound and restarts it.
 *
 * "Muted-autoplaying" is checked against the video's actual `muted` state, not just the
 * startedMuted flag — the user can unmute via the always-visible native controls at any point,
 * and trusting a stale flag would restart a video they're already mid-way through watching.
 */
export function usePromoteMutedVideoOnClick({ videoRef, startedMuted, onPromoted, startPlayback }: Params): Result {
	const track = useTracking();

	// Clears startedMuted once the video is genuinely unmuted — by our own promote logic, or by the
	// viewer using the native controls. Only on an actual unmute, though: a volume change that
	// leaves it muted (e.g. dragging the slider while muted) would otherwise clear the flag and
	// quietly switch off click-to-unmute while the video is still silent.
	useEffect(() => {
		const video = videoRef.current;
		if (!video) return undefined;

		const handleVolumeChange = () => {
			if (startedMuted && !video.muted) {
				onPromoted();
			}
		};

		video.addEventListener('volumechange', handleVolumeChange);
		return () => video.removeEventListener('volumechange', handleVolumeChange);
	}, [videoRef, startedMuted, onPromoted]);

	const promoteToSoundAndRestart = useCallback(() => {
		const video = videoRef.current;
		if (video) {
			video.muted = false;
			video.currentTime = 0;
		}
		onPromoted();
		startPlayback(() => {
			void track('ambassadors_video_play');
		});
	}, [videoRef, onPromoted, startPlayback, track]);

	// startedMuted alone isn't proof: it isn't updated by native-control interactions, and the
	// volumechange listener above clears it only eventually — this direct check covers the gap.
	const isGenuinelyStillMuted = useCallback(
		() => startedMuted && videoRef.current?.muted !== false,
		[startedMuted, videoRef],
	);

	const handlePlayClick = useCallback(() => {
		if (isGenuinelyStillMuted()) {
			promoteToSoundAndRestart();
			return;
		}
		startPlayback(() => {
			void track('ambassadors_video_play');
		});
	}, [isGenuinelyStillMuted, promoteToSoundAndRestart, startPlayback, track]);

	const handleVideoClick = useCallback(() => {
		if (!isGenuinelyStillMuted()) return;
		promoteToSoundAndRestart();
	}, [isGenuinelyStillMuted, promoteToSoundAndRestart]);

	return { handlePlayClick, handleVideoClick };
}
